import { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { TextInput, Textarea, Select, NumberInput, Switch, Button, Title, Stack, Group, Text, Badge } from '@mantine/core'
import { useForm } from '@mantine/form'
import { notifications } from '@mantine/notifications'
import ImageUpload from '@/components/ImageUpload'
import VideoUpload from '@/components/VideoUpload'
import { api } from '@/lib/api'
import { formatName } from '@/lib/format'
import { uploadFile } from '@/lib/upload'

const transcodingLabels: Record<string, { label: string; color: string }> = {
  pending: { label: 'Хүлээгдэж буй', color: 'yellow' },
  processing: { label: 'Боловсруулж байна', color: 'blue' },
  completed: { label: 'Бэлэн', color: 'green' },
  failed: { label: 'Алдаа гарсан', color: 'red' },
}

export default function VideoForm() {
  const { id } = useParams()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [capturingThumb, setCapturingThumb] = useState(false)
  const [teachers, setTeachers] = useState<{ value: string; label: string }[]>([])
  const [categories, setCategories] = useState<string[]>([])
  const [transcodingStatus, setTranscodingStatus] = useState('')
  const [transcodingError, setTranscodingError] = useState('')
  const [newThumbnailUrl, setNewThumbnailUrl] = useState('')
  const [useNewThumbnail, setUseNewThumbnail] = useState(false)
  const form = useForm({
    initialValues: {
      title: '',
      description: '',
      teacherId: '',
      duration: '',
      category: 'Номлол',
      price: 0,
      isFeatured: false,
      isMemberOnly: false,
      thumbnailUrl: '',
      videoUrl: '',
    },
  })

  useEffect(() => {
    api.admin.getTeachers({ limit: '100' }).then((res) => {
      setTeachers(
        res.data.map((t) => {
          const user = t.userId as Record<string, unknown> | undefined
          return { value: t._id as string, label: formatName(user?.fullName as string) || 'Unknown' }
        })
      )
    })
    api.admin.getCategories().then((res) => {
      setCategories(res.videoCategories)
    }).catch(() => {})
  }, [])

  useEffect(() => {
    if (id) {
      fetch(`${import.meta.env.VITE_API_URL || '/api/v1'}/videos/${id}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` },
      })
        .then((r) => r.json())
        .then((video) => {
          const teacherId = typeof video.teacherId === 'object' ? video.teacherId._id : video.teacherId
          form.setValues({
            title: video.title || '',
            description: video.description || '',
            teacherId: teacherId || '',
            duration: video.duration || '',
            category: video.category || 'Номлол',
            price: video.price || 0,
            isFeatured: video.isFeatured || false,
            isMemberOnly: video.isMemberOnly || false,
            thumbnailUrl: video.thumbnailUrl || '',
            videoUrl: video.videoUrl || '',
          })
          setTranscodingStatus(video.transcodingStatus || '')
          setTranscodingError(video.transcodingError || '')
        })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  const captureThumbnail = async (file: File) => {
    setCapturingThumb(true)
    try {
      const url = URL.createObjectURL(file)
      const video = document.createElement('video')
      video.preload = 'auto'
      video.muted = true
      video.playsInline = true
      video.src = url

      const blob = await new Promise<Blob>((resolve, reject) => {
        video.addEventListener('loadedmetadata', () => {
            const totalSecs = Math.floor(video.duration)
            const mins = Math.floor(totalSecs / 60)
            const secs = totalSecs % 60
            form.setFieldValue('duration', `${mins}:${secs.toString().padStart(2, '0')}`)
        })
        video.addEventListener('loadeddata', () => {
          video.currentTime = Math.min(1, video.duration * 0.1)
        })
        video.addEventListener('seeked', () => {
          const canvas = document.createElement('canvas')
          canvas.width = video.videoWidth
          canvas.height = video.videoHeight
          const ctx = canvas.getContext('2d')
          if (!ctx) { reject(new Error('Canvas not supported')); return }
          ctx.drawImage(video, 0, 0)
          URL.revokeObjectURL(url)
          canvas.toBlob((b) => {
            if (b) resolve(b)
            else reject(new Error('Failed to capture frame'))
          }, 'image/jpeg', 0.85)
        })
        video.addEventListener('error', () => {
          URL.revokeObjectURL(url)
          reject(new Error('Video load error'))
        })
      })

        const thumbFile = new File([blob], 'thumbnail.jpg', { type: 'image/jpeg' })
        const result = await uploadFile({ file: thumbFile, type: 'image' })
        setNewThumbnailUrl(result.url)
        setUseNewThumbnail(true)
        notifications.show({ title: 'Амжилттай', message: 'Шинэ thumbnail бэлэн боллоо', color: 'green' })
    } catch {
      // silent fail — user can still upload thumbnail manually
    } finally {
      setCapturingThumb(false)
    }
  }

  const handleSubmit = async (values: typeof form.values) => {
    setLoading(true)
    try {
       const finalThumbnail = useNewThumbnail && newThumbnailUrl
        ? newThumbnailUrl
        : values.thumbnailUrl
      const payload = { ...values, thumbnailUrl: finalThumbnail}
      if (id) {
        await api.admin.updateVideo(id, payload)
        notifications.show({ title: 'Амжилттай', message: 'Видео шинэчлэгдлээ', color: 'green' })
      } else {
        const created = await api.admin.createVideo(values) as Record<string, unknown>
        // Шинэ видео үүсгэсний дараа videoUrl байвал transcoding эхлүүлнэ
        if (values.videoUrl && created._id) {
          await api.admin.updateVideo(created._id as string, { retranscode: true })
        }
        notifications.show({ title: 'Амжилттай', message: 'Видео үүсгэгдлээ', color: 'green' })
      }
      navigate('/videos')
    } catch {
      notifications.show({ title: 'Алдаа', message: 'Алдаа гарлаа', color: 'red' })
    } finally {
      setLoading(false)
    }
  }

  const statusInfo = transcodingLabels[transcodingStatus]

  return (
    <div>
      <Group justify="space-between" mb="lg">
        <Title order={2}>{id ? 'Видео засах' : 'Шинэ видео'}</Title>
        {statusInfo && (
          <Badge variant="light" color={statusInfo.color} size="lg">
            {statusInfo.label}
          </Badge>
        )}
      </Group>
      {transcodingStatus === 'failed' && (
        <Stack gap={4} mb="md">
          <Text size="sm" c="red">{transcodingError || 'Transcode амжилтгүй боллоо.'}</Text>
          <Text size="xs" c="dimmed">Видео файлаа дахин оруулж, "Дахин transcode" товч дарна уу.</Text>
        </Stack>
      )}
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack maw={600}>
          <TextInput label="Гарчиг" required {...form.getInputProps('title')} />
          <Textarea label="Тайлбар" autosize minRows={3} {...form.getInputProps('description')} />
          <Select label="Багш" data={teachers} required searchable {...form.getInputProps('teacherId')} />
          <Group grow>
            <TextInput label="Хугацаа" readOnly placeholder="45:20" {...form.getInputProps('duration')} />
            <NumberInput label="Үнэ (₮)" min={0} {...form.getInputProps('price')} />
          </Group>
          <Select label="Ангилал" data={categories} {...form.getInputProps('category')} />
          <Switch label="Онцлох" {...form.getInputProps('isFeatured', { type: 'checkbox' })} />
          <Switch label="Зөвхөн гишүүн" {...form.getInputProps('isMemberOnly', { type: 'checkbox' })} />
          <div>
            <label style={{ fontSize: 14, fontWeight: 500, marginBottom: 4, display: 'block' }}>Видео файл</label>
            <VideoUpload
              value={form.values.videoUrl}
              onChange={(url) => form.setFieldValue('videoUrl', url)}
              onFileSelected={captureThumbnail}
              videoId={id}
            />
          </div>
          <div>
  <label style={{ fontSize: 14, fontWeight: 500, marginBottom: 4, display: 'block' }}>Зураг (thumbnail)</label>
  {capturingThumb && <Text size="xs" c="dimmed" mb={4}>Видеоноос thumbnail авч байна...</Text>}

  {/* Хуучин болон шинэ thumbnail сонголт */}
  {id && newThumbnailUrl && (
    <div style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
      <div
        onClick={() => setUseNewThumbnail(false)}
        style={{
          flex: 1, cursor: 'pointer', borderRadius: 8, overflow: 'hidden',
          border: !useNewThumbnail ? '2px solid #228be6' : '2px solid #dee2e6'
        }}
      >
        <Text size="xs" ta="center" py={4} fw={500} c={!useNewThumbnail ? 'blue' : 'dimmed'}>
          {!useNewThumbnail ? '✓ Хуучин (сонгогдсон)' : 'Хуучин'}
        </Text>
        <img src={form.values.thumbnailUrl} style={{ width: '100%', aspectRatio: '16/9', objectFit: 'cover' }} />
      </div>
      <div
        onClick={() => setUseNewThumbnail(true)}
        style={{
          flex: 1, cursor: 'pointer', borderRadius: 8, overflow: 'hidden',
          border: useNewThumbnail ? '2px solid #228be6' : '2px solid #dee2e6'
        }}
      >
        <Text size="xs" ta="center" py={4} fw={500} c={useNewThumbnail ? 'blue' : 'dimmed'}>
          {useNewThumbnail ? '✓ Шинэ (сонгогдсон)' : 'Шинэ'}
        </Text>
        <img src={newThumbnailUrl} style={{ width: '100%', aspectRatio: '16/9', objectFit: 'cover' }} />
      </div>
    </div>
  )}

  <ImageUpload
    value={useNewThumbnail ? newThumbnailUrl : form.values.thumbnailUrl}
    onChange={(url) => {
      if (useNewThumbnail) setNewThumbnailUrl(url)
      else form.setFieldValue('thumbnailUrl', url)
    }}
  />
</div>
          <Group>
            <Button type="submit" loading={loading}>{id ? 'Хадгалах' : 'Үүсгэх'}</Button>
            <Button variant="default" onClick={() => navigate('/videos')}>Буцах</Button>
            {id && transcodingStatus !== 'processing' && form.values.videoUrl && (
              <Button
                variant="light"
                color="blue"
                onClick={async () => {
                  await api.admin.updateVideo(id, { retranscode: true })
                  setTranscodingStatus('pending')
                  notifications.show({ title: 'Амжилттай', message: 'Transcode эхэллээ', color: 'blue' })
                }}
              >
                Дахин transcode
              </Button>
            )}
          </Group>
        </Stack>
      </form>
    </div>
  )
}
